import React, { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { identity, equals } from 'ramda';
import { usePrevious } from 'js/hooks/usePrevious';
import { clsx, clsb } from 'js/utils/classNames.util';

import * as Types from './types';
import { Control } from '../Control';
import withField from '../withField';
import * as hocs from './hocs/withFieldProps';
import { EDITABLE_DD_MODES } from './constants';
import DropDownIcon from '../Icons/DropDownIcon';
import ValidationIcon from '../Icons/ValidationIcon';

import style from './EditableDropDown.scss';

const EditableDropDownBare: React.FC<Types.IEditableDropDownProps> = (props) => {
    const {
        /* rff render props */
        input,
        meta,

        /* user provided props */
        id,
        name = '',
        hint = '',
        label = '',
        bottomHint,
        dataList = [],
        classNames = {},
        placeholder = '',
        disabled = false,
        infoTooltip = '',
        inputMode = 'text',
        onClick = identity,
        currencySymbol = '',
        displayValuesMap = {},
        mode = EDITABLE_DD_MODES.ALL,
        'aria-label': ariaLabel = '',
        className: rootClassName = ''
    } = props;
    const { value, onFocus, onChange, onBlur } = input;
    const { active, error, touched, submitting } = meta || {};
    const selectRef = useRef<HTMLSelectElement>(null);
    const prevSelect = usePrevious(value.select);
    const prevDataList = usePrevious(dataList);
    const enableInputText = mode !== EDITABLE_DD_MODES.LIST_ONLY;

    const className = clsx([
        rootClassName,
        !enableInputText ? clsb([style, classNames], ['readOnly']) : '',
        clsb([style, classNames], ['control']),
        'cms-editable-drop-down'
    ]);

    useEffect(() => {
        const isDataListEquals = equals(dataList, prevDataList);

        if (selectRef.current && (value.select !== prevSelect || !isDataListEquals)) {
            selectRef.current.value = value.select;
        }
    }, [value.select, prevSelect, dataList, prevDataList]);

    const onChangeHandler = useCallback((event: { target: { value: string } }) => {
        const { target: { value: val } } = event;

        if (selectRef.current) {
            selectRef.current.value = val;
        }

        onChange(event);
    }, [onChange]);

    const preparedDataList = useMemo(() => {
        return dataList.map((item: string | { value: string; displayValue: string }) =>
            typeof item !== 'object'
                ? {
                    displayValue: item in displayValuesMap
                        ? displayValuesMap[item as keyof typeof displayValuesMap]
                        : item,
                    value: item
                }
                : item
        );
    }, [dataList, displayValuesMap]);

    return (
        <Control
            id={id}
            meta={meta}
            hint={hint}
            label={label}
            animate={true}
            readOnly={false}
            onClick={onClick}
            className={className}
            classNames={classNames}
            bottomHint={bottomHint}
            infoTooltip={infoTooltip}
            {...currencySymbol && { 'data-currency-symbol': currencySymbol }}
        >
            <input
                id={id}
                type="text"
                name={name}
                onBlur={onBlur}
                onFocus={onFocus}
                autoComplete="off"
                value={value.input}
                inputMode={inputMode}
                aria-label={ariaLabel}
                placeholder={placeholder}
                onChange={onChangeHandler}
                readOnly={!enableInputText}
                disabled={disabled || submitting}
            />

            {touched && !active && (
                <ValidationIcon
                    animate={true}
                    isValid={!error}
                    aria-label={error}
                    tabIndex={error ? 0 : -1}
                    className={clsx([clsb([style, classNames], ['validationIcon']), 'cms-editable-drop-down-icon'])}
                />
            )}

            {mode !== EDITABLE_DD_MODES.TEXT_ONLY && (
                <Fragment>
                    <select
                        name={name}
                        ref={selectRef}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        onChange={onChange}
                        id={`${id}-select`}
                        inputMode={inputMode}
                        aria-label={ariaLabel}
                        disabled={disabled || submitting}
                    >
                        {preparedDataList.map(({ value: v, displayValue }, index) => (
                            <option key={`edd-${index}`} value={v}>
                                { displayValue }
                            </option>
                        ))}
                    </select>
                    <DropDownIcon
                        separator={true}
                        disabled={disabled}
                        classNames={classNames}
                        className="cms-editable-drop-down-icon"
                    />
                </Fragment>
            )}
        </Control>
    );
};

export const EditableDropDown = hocs.withFieldProps(withField(EditableDropDownBare));
